// @ts-nocheck
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { LuRefreshCw } from "react-icons/lu";
import { msalInstance } from "src/lib/msal";
import { baseUriSF } from "../../../config";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";

const SFAttachments = ({ orderNumber }) => {
    const [uploading, setUploading] = useState(false);
    const [attachments, setAttachments] = useState<
        { FileUrl: string; FileName: string; Description: string }[]
    >([]);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const accountEnhanced = useAccountEnhanced();

    useEffect(() => {
        handleGetAttachments();
    }, []);

    const serviceFailureApiInstance = axios.create({
        baseURL: baseUriSF,
    });

    serviceFailureApiInstance.interceptors.request.use(
        async (config) => {
            const account = msalInstance.getAllAccounts()[0];
            if (account) {
                const { accessToken } = await msalInstance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: account,
                });

                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    const handleGetAttachments = async () => {
        try {
            if (orderNumber) {
                const response = await serviceFailureApiInstance.get(
                    `/${orderNumber}/attachments`,
                );

                if (response.data.Status === "OK") {
                    setAttachments(response.data.Data);
                }
            }
        } catch (error) {
            console.error("Error fetching attachments: ", error);
            setError("Error fetching attachments");
        }
    };

    const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files
            ? event.dataTransfer.files[0]
            : null;
        if (file) {
            uploadFile(file);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDivClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        if (file) {
            uploadFile(file);
            event.target.files = null;
        }
    };

    const uploadFile = async (file: File) => {
        setUploading(true);
        setError(null);

        const fileBytes = await new Promise<number[]>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const arrayBuffer = reader.result;
                const bytes = new Uint8Array(arrayBuffer as ArrayBuffer);
                resolve(Array.from(bytes));
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
        try {
            const response = await serviceFailureApiInstance.post(
                `/${orderNumber}/attachments`,
                {
                    FileName: file.name,
                    Description: file.name,
                    UploadedBy: accountEnhanced.me.employeeCode.toUpperCase(),
                    FileData: Array.from(fileBytes),
                },
            );

            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setAttachments((prevAttachments) => [
                ...prevAttachments.filter(
                    (attachment) => attachment.FileName !== file.name,
                ),
                { FileName: file.name, FileUrl: "", Description: file.name },
            ]);
        } catch (error) {
            console.error("Error uploading file:", error);
            setError("Error uploading file");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAttachment = async (fileName: string) => {
        try {
            const response = await serviceFailureApiInstance.delete(
                `/${orderNumber}/attachment/${fileName}`,
            );
        } catch (error) {
            console.error("Error deleting attachment: ", error);
            setError("Error deleting attachment.");
        }
        setAttachments(
            attachments.filter(
                (attachment) => attachment.FileName !== fileName,
            ),
        );
    };

    return (
        <div>
            <div className="card-title">Attachments</div>
            <div
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                onClick={handleDivClick}
                style={{
                    border: "2px dashed #cccccc",
                    padding: "20px",
                    textAlign: "center",
                    marginTop: "10px",
                }}
            >
                Drag and drop a file here or click to select a file
            </div>
            <input
                type="file"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: "none" }}
            />
            {uploading && <p>Uploading...</p>}
            {error && (
                <div style={{ color: "red", marginTop: "10px" }}>{error}</div>
            )}
            <div style={{ marginTop: "10px" }}>
                <h3>
                    Submitted:{" "}
                    <button
                        onClick={handleGetAttachments}
                        style={{ marginTop: "20px" }}
                        className="btn btn-sm btn-primary"
                    >
                        <LuRefreshCw />
                    </button>
                </h3>
                <div>
                    {attachments &&
                        attachments.map((attachment, index) => (
                            <div key={index} className="mt-4">
                                <button
                                    onClick={() =>
                                        handleDeleteAttachment(
                                            attachment.FileName,
                                        )
                                    }
                                    className="btn btn-sm btn-primary"
                                >
                                    Delete
                                </button>
                                <a
                                    href={attachment.FileUrl}
                                    target="_blank"
                                    className="align-middle ml-3 break-words inline-block w-64"
                                >
                                    {attachment.Description}
                                </a>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default SFAttachments;
